import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Select,
    MenuItem,
    Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Availability to start choices
const AVAILABILITY_TO_START = [
    { value: 'IMMEDIATELY', label: 'Immediately' },
    { value: 'ONE_WEEK', label: 'In 1 week' },
    { value: 'TWO_WEEKS', label: 'In 2 weeks' },
    { value: 'ONE_MONTH', label: 'In 1 month' },
    { value: 'TWO_MONTHS', label: 'In 2 months' },
    { value: 'THREE_MONTHS', label: 'In 3 months' },
];

// Timezone options
const TIMEZONE_OPTIONS = [
    { value: 'US/Pacific', label: '(GMT-08:00) Pacific Time (US & Canada)' },
    { value: 'US/Mountain', label: '(GMT-07:00) Mountain Time (US & Canada)' },
    { value: 'US/Central', label: '(GMT-06:00) Central Time (US & Canada)' },
    { value: 'US/Eastern', label: '(GMT-05:00) Eastern Time (US & Canada)' },
    { value: 'America/Sao_Paulo', label: '(GMT-03:00) Sao Paulo' },
    { value: 'UTC', label: '(GMT+00:00) UTC' },
    { value: 'Europe/London', label: '(GMT+00:00) London' },
    { value: 'Europe/Paris', label: '(GMT+01:00) Paris, Berlin' },
    { value: 'Europe/Helsinki', label: '(GMT+02:00) Helsinki, Kiev' },
    { value: 'Asia/Dubai', label: '(GMT+04:00) Dubai' },
    { value: 'Asia/Kolkata', label: '(GMT+05:30) India Standard Time' },
    { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore, Hong Kong' },
    { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo, Seoul' },
    { value: 'Australia/Sydney', label: '(GMT+11:00) Sydney' },
];

// Days of the week
const DAYS = [
    { id: 'sunday', label: 'S', full: 'Sunday' },
    { id: 'monday', label: 'M', full: 'Monday' },
    { id: 'tuesday', label: 'T', full: 'Tuesday' },
    { id: 'wednesday', label: 'W', full: 'Wednesday' },
    { id: 'thursday', label: 'T', full: 'Thursday' },
    { id: 'friday', label: 'F', full: 'Friday' },
    { id: 'saturday', label: 'S', full: 'Saturday' },
];

// Hours of the week for the schedule grid
const HOURS = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i === 0 ? '12' : i > 12 ? i - 12 : i}${i < 12 ? 'am' : 'pm'}`,
}));

// Mercor-style input classes
const inputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& fieldset': {
            borderColor: '#d1d5db',
        },
        '&:hover fieldset': {
            borderColor: '#d1d5db',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#4f46e5',
            borderWidth: '2px',
        },
    },
    '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
    },
};

// Error input styles
const errorInputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        fontSize: '0.875rem',
        backgroundColor: '#fef2f2',
        '& fieldset': {
            borderColor: '#fca5a5',
            borderWidth: '1.5px',
        },
        '&:hover fieldset': {
            borderColor: '#f87171',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#ef4444',
            borderWidth: '2px',
        },
    },
    '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
        color: '#dc2626',
    },
};

const FormSection = ({ title, children, sidebarWidth = '200px' }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 5 }, mb: 4 }}>
        <Box sx={{ width: { md: sidebarWidth }, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
            {children}
        </Box>
    </Box>
);

const AvailabilityTab = ({ userProfile, userId, authAxios, onUpdate, setError }) => {
    const [savingAvailability, setSavingAvailability] = useState(false);
    const [savingWorkingHours, setSavingWorkingHours] = useState(false);
    const [showHoursError, setShowHoursError] = useState(false);

    const [formData, setFormData] = useState({
        availability_to_start: '',
        preferred_weekly_hours: '',
        timezone: '',
    });

    // Working hours schedule - each day has start and end times
    const [schedule, setSchedule] = useState({
        sunday: { active: false, start: 9, end: 17 },
        monday: { active: true, start: 9, end: 17 },
        tuesday: { active: true, start: 9, end: 17 },
        wednesday: { active: true, start: 9, end: 17 },
        thursday: { active: true, start: 9, end: 17 },
        friday: { active: true, start: 9, end: 17 },
        saturday: { active: false, start: 9, end: 17 },
    });

    // Initialize form data from userProfile
    useEffect(() => {
        if (userProfile) {
            setFormData({
                availability_to_start: userProfile.availability_to_start || '',
                preferred_weekly_hours: userProfile.preferred_weekly_hours || '',
                timezone: userProfile.timezone || '',
            });
            // Initialize schedule from working_hours array
            if (userProfile.working_hours && Array.isArray(userProfile.working_hours)) {
                const scheduleData = {};
                userProfile.working_hours.forEach(wh => {
                    const dayKey = wh.day_of_week.toLowerCase();
                    scheduleData[dayKey] = {
                        active: wh.is_available,
                        start: wh.start_time ? parseInt(wh.start_time.split(':')[0]) : 9,
                        end: wh.end_time ? parseInt(wh.end_time.split(':')[0]) : 17,
                        id: wh.id,
                    };
                });
                setSchedule(prev => ({ ...prev, ...scheduleData }));
            }
        }
    }, [userProfile]);

    // Handle input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing in preferred_weekly_hours
        if (field === 'preferred_weekly_hours' && value) {
            setShowHoursError(false);
        }
    };

    // Toggle day active state
    const toggleDayActive = (dayId) => {
        setSchedule(prev => ({
            ...prev,
            [dayId]: { ...prev[dayId], active: !prev[dayId].active },
        }));
    };

    // Save Availability section handler
    const handleSaveAvailability = async () => {
        // Validate preferred_weekly_hours is filled
        if (!formData.preferred_weekly_hours) {
            setShowHoursError(true);
            if (setError) setError('Preferred Weekly Hours is a mandatory field');
            return;
        }
        
        setSavingAvailability(true);
        try {
            await authAxios.patch('/self/', {
                availability_to_start: formData.availability_to_start,
                preferred_weekly_hours: formData.preferred_weekly_hours,
                timezone: formData.timezone,
            });

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to save availability:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to save availability changes';
            if (setError) setError(errorMsg);
        } finally {
            setSavingAvailability(false);
        }
    };

    // Save Working Hours section handler
    const handleSaveWorkingHours = async () => {
        setSavingWorkingHours(true);
        try {
            // Update or create working hours for each day
            const dayOfWeekMap = {
                sunday: 'SUNDAY',
                monday: 'MONDAY',
                tuesday: 'TUESDAY',
                wednesday: 'WEDNESDAY',
                thursday: 'THURSDAY',
                friday: 'FRIDAY',
                saturday: 'SATURDAY',
            };

            for (const [dayKey, dayData] of Object.entries(schedule)) {
                const payload = {
                    day_of_week: dayOfWeekMap[dayKey],
                    is_available: dayData.active,
                    start_time: dayData.active ? `${String(dayData.start).padStart(2, '0')}:00:00` : null,
                    end_time: dayData.active ? `${String(dayData.end).padStart(2, '0')}:00:00` : null,
                };

                if (dayData.id) {
                    // Update existing working hours
                    await authAxios.patch(`/working-hours/${dayData.id}/`, payload);
                } else {
                    // Create new working hours
                    try {
                        const response = await authAxios.post('/working-hours/', payload);
                        // Store the ID for future updates
                        setSchedule(prev => ({
                            ...prev,
                            [dayKey]: { ...prev[dayKey], id: response.data.id },
                        }));
                    } catch (err) {
                        // If it already exists (unique constraint), fetch and update
                        if (err.response?.status === 400) {
                            const existingHours = await authAxios.get('/working-hours/');
                            const existing = existingHours.data.find(wh => 
                                wh.day_of_week === dayOfWeekMap[dayKey]
                            );
                            if (existing) {
                                await authAxios.patch(`/working-hours/${existing.id}/`, payload);
                                setSchedule(prev => ({
                                    ...prev,
                                    [dayKey]: { ...prev[dayKey], id: existing.id },
                                }));
                            }
                        } else {
                            throw err;
                        }
                    }
                }
            }

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to save working hours:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to save working hours';
            if (setError) setError(errorMsg);
        } finally {
            setSavingWorkingHours(false);
        }
    };

    return (
        <Box sx={{ maxWidth: '900px', mx: 'auto', py: 4 }}>
            {/* Availability Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Availability
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection title="Availability to Start">
                    <Select
                        fullWidth
                        size="small"
                        value={formData.availability_to_start}
                        onChange={(e) => handleInputChange('availability_to_start', e.target.value)}
                        displayEmpty
                        sx={{
                            ...inputStyles['& .MuiOutlinedInput-root'],
                            fontSize: '0.875rem',
                        }}
                        IconComponent={ExpandMoreIcon}
                    >
                        <MenuItem value="" disabled>
                            <Typography sx={{ color: '#9ca3af' }}>Select when you can start</Typography>
                        </MenuItem>
                        {AVAILABILITY_TO_START.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormSection>

                <FormSection title="Preferred Weekly Hours *">
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Ex: 40"
                        value={formData.preferred_weekly_hours}
                        onChange={(e) => handleInputChange('preferred_weekly_hours', e.target.value)}
                        sx={showHoursError && !formData.preferred_weekly_hours ? errorInputStyles : inputStyles}
                        inputProps={{ min: 0, max: 168 }}
                        error={showHoursError && !formData.preferred_weekly_hours}
                        helperText={showHoursError && !formData.preferred_weekly_hours ? 'This field is mandatory' : ''}
                    />
                </FormSection>

                <FormSection title="Timezone">
                    <Select
                        fullWidth
                        size="small"
                        value={formData.timezone}
                        onChange={(e) => handleInputChange('timezone', e.target.value)}
                        displayEmpty
                        sx={{
                            ...inputStyles['& .MuiOutlinedInput-root'],
                            fontSize: '0.875rem',
                        }}
                        IconComponent={ExpandMoreIcon}
                    >
                        <MenuItem value="" disabled>
                            <Typography sx={{ color: '#9ca3af' }}>Select Timezone</Typography>
                        </MenuItem>
                        {TIMEZONE_OPTIONS.map(tz => (
                            <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                        ))}
                    </Select>
                </FormSection>

                {/* Save Button for Availability */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSaveAvailability}
                        disabled={savingAvailability}
                        sx={{
                            bgcolor: '#4f46e5',
                            textTransform: 'none',
                            px: 4,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {savingAvailability ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {/* Working Hours Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Working Hours
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Set times in your local timezone
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection title="Working Days">
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {DAYS.map((day) => (
                            <Box
                                key={day.id}
                                onClick={() => toggleDayActive(day.id)}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s',
                                    bgcolor: schedule[day.id].active ? '#4f46e5' : '#f3f4f6',
                                    color: schedule[day.id].active ? 'white' : '#6b7280',
                                    '&:hover': {
                                        bgcolor: schedule[day.id].active ? '#4338ca' : '#e5e7eb',
                                    },
                                }}
                                title={day.full}
                            >
                                {day.label}
                            </Box>
                        ))}
                    </Box>
                </FormSection>

                {/* Schedule Grid */}
                <Box sx={{ mt: 4 }}>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>
                        Daily Schedule
                    </Typography>
                    
                    {DAYS.map((day) => (
                        schedule[day.id].active && (
                            <Box key={day.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography sx={{ 
                                    width: 100, 
                                    fontSize: '0.875rem', 
                                    fontWeight: 500, 
                                    color: '#374151'
                                }}>
                                    {day.full}
                                </Typography>
                                
                                <Select
                                    size="small"
                                    value={schedule[day.id].start}
                                    onChange={(e) => setSchedule(prev => ({
                                        ...prev,
                                        [day.id]: { ...prev[day.id], start: e.target.value },
                                    }))}
                                    sx={{
                                        ...inputStyles['& .MuiOutlinedInput-root'],
                                        width: 120,
                                        fontSize: '0.875rem',
                                    }}
                                    IconComponent={ExpandMoreIcon}
                                >
                                    {HOURS.map(hour => (
                                        <MenuItem key={hour.value} value={hour.value}>{hour.label}</MenuItem>
                                    ))}
                                </Select>
                                
                                <Typography sx={{ color: '#6b7280' }}>to</Typography>
                                
                                <Select
                                    size="small"
                                    value={schedule[day.id].end}
                                    onChange={(e) => setSchedule(prev => ({
                                        ...prev,
                                        [day.id]: { ...prev[day.id], end: e.target.value },
                                    }))}
                                    sx={{
                                        ...inputStyles['& .MuiOutlinedInput-root'],
                                        width: 120,
                                        fontSize: '0.875rem',
                                    }}
                                    IconComponent={ExpandMoreIcon}
                                >
                                    {HOURS.map(hour => (
                                        <MenuItem key={hour.value} value={hour.value}>{hour.label}</MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        )
                    ))}
                </Box>

                {/* Save Button for Working Hours */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                    <Button
                        variant="contained"
                        onClick={handleSaveWorkingHours}
                        disabled={savingWorkingHours}
                        sx={{
                            bgcolor: '#4f46e5',
                            textTransform: 'none',
                            px: 4,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {savingWorkingHours ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default AvailabilityTab;
